import { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import { axiosInstance } from '../../utils/axiosInstance';
import {
  CycleCatalogItem,
  GetPriceResult,
  MachineKind,
  axiosErrorMessage,
  cycleLabel,
  defaultTemperatureId,
  filterCycleCatalog,
} from './machineControlTypes';
import './machineControlModal.scss';

export type MachineChangeCycleModalTarget = {
  macId: string;
  name: string;
  kind: MachineKind;
};

type Props = {
  open: boolean;
  target: MachineChangeCycleModalTarget | null;
  busy?: boolean;
  error?: string | null;
  requestedBy: string;
  onClose: () => void;
  onSubmit: (payload: {
    cycleId: string;
    temperatureId?: string | null;
  }) => void;
};

const MachineChangeCycleModal = ({
  open,
  target,
  busy,
  error,
  requestedBy,
  onClose,
  onSubmit,
}: Props) => {
  const macId = target?.macId || '';
  const kind = target?.kind || 'unknown';
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [cycles, setCycles] = useState<CycleCatalogItem[]>([]);
  const [selectedCycleId, setSelectedCycleId] = useState('');
  const [temperatureId, setTemperatureId] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !macId) return;

    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setCycles([]);
    setSelectedCycleId('');
    setTemperatureId(null);

    axiosInstance
      .post(`/machine/${encodeURIComponent(macId)}/get-price`, {
        requested_by: requestedBy,
      })
      .then((res) => {
        if (cancelled) return;
        const data = res.data as GetPriceResult;
        const catalog = filterCycleCatalog(data.cycles?.cycles);
        setCycles(catalog);
        const currentId = data.pricing?.selectedCycle?.id || '';
        if (catalog.length > 0) {
          const preferred =
            catalog.find((c) => c.id === currentId) || catalog[0];
          setSelectedCycleId(preferred.id);
          setTemperatureId(
            kind === 'washer' ? defaultTemperatureId(preferred) : null
          );
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(axiosErrorMessage(err, 'Failed to load cycles'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, macId, requestedBy, kind]);

  const selectedCycle = useMemo(
    () => cycles.find((c) => c.id === selectedCycleId),
    [cycles, selectedCycleId]
  );

  const temperatures = selectedCycle?.temperatures || [];

  const canSubmit =
    !busy && !loading && !!macId && !!selectedCycleId && kind !== 'unknown';

  const handleSelectCycle = (cycle: CycleCatalogItem) => {
    setSelectedCycleId(cycle.id);
    if (kind === 'washer') {
      setTemperatureId(defaultTemperatureId(cycle));
    }
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      cycleId: selectedCycleId,
      temperatureId: kind === 'washer' ? temperatureId : null,
    });
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Change cycle — {target?.name || 'machine'}
        <IconButton aria-label="Close" onClick={onClose} disabled={busy} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className="machine-control-modal-content">
        <p className="machine-control-meta">
          {kind === 'dryer'
            ? 'Set dryer heat level without starting.'
            : kind === 'washer'
              ? 'Set wash cycle (and temperature) without starting.'
              : 'Machine type unknown — cannot change cycle.'}
        </p>

        {loading && (
          <div className="machine-control-empty">
            <CircularProgress size={22} /> Loading cycles…
          </div>
        )}

        {(loadError || error) && (
          <Alert severity="error">{loadError || error}</Alert>
        )}

        {!loading && !loadError && kind !== 'unknown' && (
          <>
            <section className="machine-control-section">
              <h3>{kind === 'dryer' ? 'Heat' : 'Cycle'}</h3>
              {cycles.length === 0 ? (
                <p className="machine-control-empty">No cycles returned from Alliance.</p>
              ) : (
                <div className="machine-control-options">
                  {cycles.map((c) => (
                    <Button
                      key={c.id}
                      className="machine-control-option"
                      variant={selectedCycleId === c.id ? 'contained' : 'outlined'}
                      size="small"
                      disabled={busy}
                      onClick={() => handleSelectCycle(c)}
                    >
                      {cycleLabel(c.name, c.id)}
                    </Button>
                  ))}
                </div>
              )}
            </section>

            {kind === 'washer' && temperatures.length > 0 && (
              <section className="machine-control-section">
                <h3>Temperature</h3>
                <div className="machine-control-options">
                  {temperatures.map((t) => (
                    <Button
                      key={t.id}
                      className="machine-control-option"
                      variant={temperatureId === t.id ? 'contained' : 'outlined'}
                      size="small"
                      disabled={busy}
                      onClick={() => setTemperatureId(t.id)}
                    >
                      {cycleLabel(t.name, t.id)}
                    </Button>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {busy ? 'Applying…' : 'Apply cycle'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MachineChangeCycleModal;
